import type { CallAnalysisDealSnapshot } from '@marnic/shared';
import { MiniSection } from '../primitives/MiniSection';
import { EvidenceBadgeList } from '../EvidenceBadge';

export function DealSnapshotCard({ snapshot }: { snapshot: CallAnalysisDealSnapshot | undefined }): JSX.Element | null {
  if (!snapshot) return null;
  const hasContent =
    snapshot.stage ||
    typeof snapshot.closeProbability === 'number' ||
    snapshot.orderSize ||
    snapshot.deadline ||
    snapshot.decisionMaker ||
    snapshot.competitor ||
    snapshot.blocker;
  if (!hasContent) return null;

  const probabilityPct =
    typeof snapshot.closeProbability === 'number'
      ? Math.round(snapshot.closeProbability * 100)
      : null;

  return (
    <MiniSection title="Deal at a glance">
      <div className="grid grid-cols-2 gap-2 text-[11px]">
        {snapshot.stage && (
          <div>
            <div className="text-ink-500 text-[10px] uppercase">Stage</div>
            <div className="font-medium">{snapshot.stage}</div>
          </div>
        )}
        {probabilityPct !== null && (
          <div>
            <div className="text-ink-500 text-[10px] uppercase">Close probability</div>
            <div className="flex items-center gap-2">
              <div className="flex-1 h-1.5 rounded bg-ink-100 overflow-hidden">
                <div
                  className={`h-full ${
                    probabilityPct >= 60 ? 'bg-emerald-500'
                    : probabilityPct >= 30 ? 'bg-amber-500'
                    : 'bg-red-400'
                  }`}
                  style={{ width: `${probabilityPct}%` }}
                />
              </div>
              <span className="font-medium tabular-nums">{probabilityPct}%</span>
            </div>
          </div>
        )}
        {snapshot.orderSize && (
          <div>
            <div className="text-ink-500 text-[10px] uppercase">Order size</div>
            <div>{snapshot.orderSize}</div>
          </div>
        )}
        {snapshot.deadline && (
          <div>
            <div className="text-ink-500 text-[10px] uppercase">Deadline</div>
            <div>{snapshot.deadline}</div>
          </div>
        )}
        {snapshot.decisionMaker && (
          <div>
            <div className="text-ink-500 text-[10px] uppercase">Decision maker</div>
            <div>{snapshot.decisionMaker}</div>
          </div>
        )}
        {snapshot.competitor && (
          <div>
            <div className="text-ink-500 text-[10px] uppercase">Competitor</div>
            <div>{snapshot.competitor}</div>
          </div>
        )}
      </div>

      {snapshot.blocker && (
        <div className="rounded bg-red-50 border border-red-100 px-2 py-1 text-[11px] text-red-800">
          <span className="font-semibold">Blocker:</span> {snapshot.blocker}
        </div>
      )}

      {snapshot.supportingEvidenceIds && snapshot.supportingEvidenceIds.length > 0 && (
        <div className="flex items-center gap-2">
          <span className="text-[10px] uppercase text-ink-500">Evidence</span>
          <EvidenceBadgeList ids={snapshot.supportingEvidenceIds} />
        </div>
      )}
    </MiniSection>
  );
}
